import React, { useEffect, useState } from "react";
import { Users, Search, Plus, Trash2, Pencil, Ban, CheckCircle2, X, Loader2, AlertTriangle, Shield, CreditCard } from "lucide-react";
import { adminApi } from "../lib/api";
import { useAuth } from "../lib/auth";

const PLAN_COLOR = {
  standard: "bg-primary/10 text-primary",
  pro: "bg-accent text-[#0D1B2A]",
};

export default function AdminSuperSubscribers() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [suspendedFilter, setSuspendedFilter] = useState("");
  const [total, setTotal] = useState(0);
  const [editing, setEditing] = useState(null); // null | "new" | subscriber object
  const [selected, setSelected] = useState([]);

  const load = () => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (planFilter) params.plan = planFilter;
    if (suspendedFilter !== "") params.is_suspended = suspendedFilter === "true";
    
    adminApi.listSubscribers(params)
      .then(({ data }) => {
        setItems(data.items || []);
        setTotal(data.total || 0);
      })
      .catch((e) => setError(e?.response?.data?.detail || "Erreur"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (user?.role !== "superadmin") {
      setError("Accès réservé aux super-administrateurs");
      setLoading(false);
      return;
    }
    load();
  }, [user, search, planFilter, suspendedFilter]);

  const toggleSelect = (id) => {
    setSelected(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const bulkAction = async (action) => {
    if (selected.length === 0) return;
    if (!window.confirm(`Confirmer l'action "${action}" sur ${selected.length} abonnés ?`)) return;
    
    try {
      await adminApi.bulkAction({ action, ids: selected, confirmation: "CONFIRM" });
      setSelected([]);
      load();
    } catch (err) {
      alert(err?.response?.data?.detail || "Erreur");
    }
  };

  const remove = async (sub) => {
    if (!window.confirm(`Supprimer définitivement ${sub.full_name} (${sub.email}) ?`)) return;
    try {
      await adminApi.deleteSubscriber(sub.id);
      load();
    } catch (err) {
      alert(err?.response?.data?.detail || "Erreur");
    }
  };

  if (user?.role !== "superadmin") {
    return (
      <div className="p-4 text-sm text-[#991B1B] bg-[#FEE2E2] border border-[#FECACA] rounded-sm">
        <AlertTriangle size={14} className="inline mr-1 -mt-0.5" />
        Accès réservé aux super-administrateurs
      </div>
    );
  }

  return (
    <div data-testid="super-subscribers-page">
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent mb-2">/ gestion des abonnés</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold text-primary tracking-tight">Abonnés & Abonnements</h1>
          <p className="text-sm text-foreground/60 mt-2 max-w-2xl">
            Gérez tous les comptes abonnés de la plateforme avec des permissions complètes.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditing("new")}
          className="inline-flex items-center gap-2 bg-primary text-white px-4 py-2.5 rounded-sm text-sm font-medium hover:bg-primary-light transition-colors"
          data-testid="subs-new-btn"
        >
          <Plus size={14} /> Nouvel abonné
        </button>
      </div>

      {error && (
        <div className="mb-4 text-sm text-[#991B1B] bg-[#FEE2E2] border border-[#FECACA] px-3 py-2 rounded-sm">
          <AlertTriangle size={14} className="inline mr-1 -mt-0.5" />{error}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-primary/15 rounded-sm p-4 mb-4">
        <div className="grid md:grid-cols-4 gap-3">
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-foreground/60 mb-1">Recherche</label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Email, nom, téléphone..."
              className="w-full bg-transparent text-sm py-2 outline-none border-b border-primary/20 focus:border-primary"
              data-testid="subs-search-input"
            />
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-foreground/60 mb-1">Plan</label>
            <select
              value={planFilter}
              onChange={(e) => setPlanFilter(e.target.value)}
              className="w-full bg-transparent text-sm py-2 outline-none border-b border-primary/20 focus:border-primary"
              data-testid="subs-plan-filter"
            >
              <option value="">Tous</option>
              <option value="standard">Standard</option>
              <option value="pro">Pro</option>
            </select>
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-foreground/60 mb-1">Statut</label>
            <select
              value={suspendedFilter}
              onChange={(e) => setSuspendedFilter(e.target.value)}
              className="w-full bg-transparent text-sm py-2 outline-none border-b border-primary/20 focus:border-primary"
              data-testid="subs-status-filter"
            >
              <option value="">Tous</option>
              <option value="false">Actifs</option>
              <option value="true">Suspendus</option>
            </select>
          </div>
          <div className="flex items-end">
            <span className="text-xs text-foreground/60">
              <strong className="text-primary">{total}</strong> abonnés trouvés
            </span>
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {selected.length > 0 && (
        <div className="bg-accent/10 border border-accent/30 rounded-sm p-3 mb-4 flex items-center justify-between">
          <span className="text-sm text-[#b4551c]">
            <strong>{selected.length}</strong> sélectionné(s)
          </span>
          <div className="flex gap-2">
            <button onClick={() => bulkAction("suspend")} className="text-xs bg-accent text-[#0D1B2A] px-3 py-1.5 rounded-sm hover:opacity-90">
              Suspendre
            </button>
            <button onClick={() => bulkAction("activate")} className="text-xs bg-green-600 text-white px-3 py-1.5 rounded-sm hover:opacity-90">
              Activer
            </button>
            <button onClick={() => bulkAction("delete")} className="text-xs bg-red-600 text-white px-3 py-1.5 rounded-sm hover:opacity-90">
              Supprimer
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="text-sm text-foreground/50">Chargement…</div>
      ) : (
        <div className="bg-white border border-primary/15 rounded-sm overflow-x-auto" data-testid="subs-table">
          <table className="w-full text-sm">
            <thead className="bg-primary/5 text-primary font-mono uppercase text-[10px] tracking-widest">
              <tr>
                <th className="px-4 py-3 w-8">
                  <input
                    type="checkbox"
                    checked={selected.length === items.length && items.length > 0}
                    onChange={(e) => setSelected(e.target.checked ? items.map(i => i.id) : [])}
                  />
                </th>
                <th className="text-left px-4 py-3">Nom</th>
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3">Plan</th>
                <th className="text-left px-4 py-3">Abonnement</th>
                <th className="text-left px-4 py-3">Statut</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-foreground/50">Aucun abonné trouvé.</td></tr>
              )}
              {items.map((sub) => (
                <tr key={sub.id} className="border-t border-primary/10 hover:bg-primary/5">
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selected.includes(sub.id)}
                      onChange={() => toggleSelect(sub.id)}
                    />
                  </td>
                  <td className="px-4 py-3 font-medium text-foreground">{sub.full_name}</td>
                  <td className="px-4 py-3 font-mono text-xs text-foreground/80">
                    <div>{sub.email}</div>
                    {sub.phone && <div className="text-foreground/50 text-[10px]">{sub.phone}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm ${PLAN_COLOR[sub.plan]}`}>
                      {sub.plan} · {sub.billing}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {sub.subscription_active ? (
                      <span className="text-green-600 flex items-center gap-1">
                        <CheckCircle2 size={12} />
                        Actif
                        {sub.subscription_expires && (
                          <span className="text-foreground/40 ml-1">
                            (exp: {new Date(sub.subscription_expires).toLocaleDateString()})
                          </span>
                        )}
                      </span>
                    ) : (
                      <span className="text-foreground/40">Aucun</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {sub.is_suspended ? (
                      <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-sm">Suspendu</span>
                    ) : (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-sm">Actif</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => setEditing(sub)}
                      className="inline-flex items-center gap-1 text-xs text-primary hover:underline mr-3"
                      data-testid={`subs-edit-${sub.id}`}
                    >
                      <Pencil size={12} /> Éditer
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(sub)}
                      className="inline-flex items-center gap-1 text-xs text-[#991B1B] hover:underline"
                      data-testid={`subs-delete-${sub.id}`}
                    >
                      <Trash2 size={12} /> Supprimer
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <SubscriberModal
          subscriber={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function SubscriberModal({ subscriber, onClose, onSaved }) {
  const isNew = !subscriber;
  const [form, setForm] = useState({
    email: subscriber?.email || "",
    full_name: subscriber?.full_name || "",
    phone: subscriber?.phone || "",
    plan: subscriber?.plan || "standard",
    billing: subscriber?.billing || "monthly",
    password: "",
    is_suspended: subscriber?.is_suspended || false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      if (isNew) {
        await adminApi.createSubscriber({
          email: form.email.trim().toLowerCase(),
          full_name: form.full_name.trim(),
          phone: form.phone.trim() || null,
          plan: form.plan,
          billing: form.billing,
          password: form.password,
        });
      } else {
        const patch = {
          full_name: form.full_name.trim() || undefined,
          email: form.email.trim().toLowerCase() || undefined,
          phone: form.phone.trim() || null,
          plan: form.plan || undefined,
          billing: form.billing || undefined,
          is_suspended: form.is_suspended,
        };
        if (form.password && form.password.length >= 8) patch.password = form.password;
        await adminApi.updateSubscriber(subscriber.id, patch);
      }
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Échec");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#0D1B2A]/70 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="subscriber-modal"
    >
      <div className="relative w-full max-w-lg bg-white border border-primary/20 rounded-sm shadow-2xl p-6 md:p-8">
        <button type="button" onClick={onClose} className="absolute top-3 right-3 text-foreground/50 hover:text-foreground"><X size={16} /></button>
        <h2 className="font-display text-2xl font-semibold text-primary mb-4">
          {isNew ? "Nouvel abonné" : "Modifier l'abonné"}
        </h2>
        {err && (
          <div className="mb-3 text-xs text-[#991B1B] bg-[#FEE2E2] border border-[#FECACA] px-3 py-2 rounded-sm" data-testid="subscriber-modal-error">
            {err}
          </div>
        )}
        <form onSubmit={submit} className="space-y-3">
          <Row label="Nom complet" required>
            <input
              type="text" required value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              className="w-full bg-transparent text-sm py-2 outline-none"
              data-testid="subscriber-modal-fullname"
            />
          </Row>
          <Row label="Email" required>
            <input
              type="email" required disabled={!isNew} value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full bg-transparent text-sm py-2 outline-none disabled:opacity-60"
              data-testid="subscriber-modal-email"
            />
          </Row>
          <Row label="Téléphone (optionnel)">
            <input
              type="tel" value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="w-full bg-transparent text-sm py-2 outline-none"
              data-testid="subscriber-modal-phone"
            />
          </Row>
          <div className="grid grid-cols-2 gap-3">
            <Row label="Plan" required>
              <select
                value={form.plan}
                onChange={(e) => setForm({ ...form, plan: e.target.value })}
                className="w-full bg-transparent text-sm py-2 outline-none"
                data-testid="subscriber-modal-plan"
              >
                <option value="standard">Standard</option>
                <option value="pro">Pro</option>
              </select>
            </Row>
            <Row label="Facturation" required>
              <select
                value={form.billing}
                onChange={(e) => setForm({ ...form, billing: e.target.value })}
                className="w-full bg-transparent text-sm py-2 outline-none"
                data-testid="subscriber-modal-billing"
              >
                <option value="monthly">Mensuel</option>
                <option value="annual">Annuel</option>
              </select>
            </Row>
          </div>
          <Row label={isNew ? "Mot de passe (min 8 car.)" : "Nouveau mot de passe (laisser vide pour ne pas changer)"} required={isNew}>
            <input
              type="password" required={isNew} minLength={isNew ? 8 : undefined}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full bg-transparent text-sm py-2 outline-none font-mono"
              data-testid="subscriber-modal-password"
            />
          </Row>
          {!isNew && (
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.is_suspended}
                onChange={(e) => setForm({ ...form, is_suspended: e.target.checked })}
              />
              <span className="text-foreground/70">Compte suspendu</span>
            </label>
          )}

          <div className="flex items-center justify-end gap-2 pt-3">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-foreground/70 hover:text-primary">Annuler</button>
            <button
              type="submit" disabled={submitting}
              className="inline-flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-sm text-sm font-medium hover:bg-primary-light transition-colors disabled:opacity-60"
              data-testid="subscriber-modal-save"
            >
              {submitting ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
              {isNew ? "Créer" : "Enregistrer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Row({ label, required, children }) {
  return (
    <label className="block">
      <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-foreground/60 mb-1">
        {label}{required && <span className="text-[#E76F51]"> *</span>}
      </span>
      <div className="border border-primary/20 rounded-sm px-3 focus-within:border-primary transition-colors bg-white">{children}</div>
    </label>
  );
}
